/**
 * Phase 20 — summarize a captured `Dimensions` rollup for the wizard's
 * dimensions step, so a room scan (or any Stella-captured measurement) is
 * shown back to the customer to confirm rather than silently skipped.
 *
 * Prefers the customer-described project axes over the AI whole-room
 * figures, mirroring the pricing priority.
 */

import type { Dimensions } from "../api/types.gen";

export interface DimensionSummaryItem {
  label: string;
  value: string;
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function summarizeDimensions(
  rollup: Dimensions | null | undefined,
): DimensionSummaryItem[] {
  if (!rollup) return [];
  const items: DimensionSummaryItem[] = [];
  const floor = rollup.project_floor_sqft ?? rollup.floor_sqft;
  const wall = rollup.project_wall_sqft ?? rollup.wall_sqft;
  if (floor && floor.value > 0) {
    items.push({ label: "Floor area", value: `${fmt(floor.value)} sq ft` });
  }
  if (wall && wall.value > 0) {
    items.push({ label: "Wall area", value: `${fmt(wall.value)} sq ft` });
  }
  if (rollup.linear_ft && rollup.linear_ft.value > 0) {
    items.push({
      label: "Length",
      value: `${fmt(rollup.linear_ft.value)} linear ft`,
    });
  }
  return items;
}

/** True when the rollup carries at least one usable measurement — used as
 * a completion signal so a room scan (which populates the rollup) counts
 * as "dimensions captured" even before an explicit anchor is listed. */
export function hasAnyDimension(
  rollup: Dimensions | null | undefined,
): boolean {
  return summarizeDimensions(rollup).length > 0;
}
